import { Component } from '@angular/core';
import { CustomColumnSettings } from '@app/datatables/models/custom-column-settings';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { DatatablePage } from '@app/shared/datatable-page';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { UnsavedChangesService } from '@app/shared/services/unsaved-changes-service';
import { Translation } from '@app/translate/models/translation';
import { MetaDataProperty, singleProperty } from '@app/utils/lists/meta-list/meta-data-property';
import { isNotBlank } from '@app/utils/string';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { delay, first, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PortalTranslateService } from '../../services/portal-translate.service';
import { TranslationEditComponent } from '../translation-edit/translation-edit.component';

@UntilDestroy()
@Component({
  selector: 'portal-translation-overview',
  templateUrl: './translation-overview.component.html',
  standalone: false,
})
export class TranslationOverviewComponent extends DatatablePage<Translation> {
  public downloading: boolean = false;
  public keys: string[] = [];
  public translations$: Observable<Translation[]>;

  public metaDataProperties: MetaDataProperty[] = [
    singleProperty('overwritten', 'translations.overwritten', [
      'translations.overwritten.no',
      'translations.overwritten.yes',
    ]),
  ];

  public get languageKeys() {
    return this.portalTranslateService.languageKeys;
  }

  constructor(
    private dialogService: DialogService,
    private portalDataService: PortalDataService,
    private portalTranslateService: PortalTranslateService,
    private translateService: TranslateService,
    private unsavedChangesService: UnsavedChangesService
  ) {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();

    this.translations$ = this.portalTranslateService.fetchTranslations().pipe(
      untilDestroyed(this),
      map((translations) => translations.items)
    );
  }

  protected fetchData(): Observable<Translation[]> {
    // We delay the translations for one cycle because they are cached by using a ReplaySubject.
    // That means that if the translations have already been loaded then the subscription to the
    // observable is resolved immediately. This causes angular to throw a
    // 'ExpressionChangedAfterItHasBeenCheckedError' because the DatatablePage calls fetchData in
    // the AfterViewInit callback. By delaying the translations by one cycle we force the execution
    // to happen asynchronously.
    return this.translations$.pipe(delay(0));
  }

  protected getColumns(): CustomColumnSettings[] {
    const translations: CustomColumnSettings[] = [];

    for (const language of this.languageKeys) {
      translations.push(
        this.column<string>(`translations.${language}`, {
          render: (data, type, row: Translation) => {
            if (row.overrides[language] != null) {
              const overwrittenLabel = this.translateService.instant('translations.overwritten.indicator');

              data = `${row.overrides[language]} <small>(${overwrittenLabel})</small>`;
            }

            return data;
          },
        })
      );
    }

    return [this.outletColumn('key'), ...translations, this.outletColumn('actions')];
  }

  protected uniqueValue(entry: Translation): string {
    return entry.key;
  }

  public calculateMetaData(translation: Translation): FilterableEntry {
    const meta = {
      overwritten: 'translations.overwritten.' + (translation.overwritten ? 'yes' : 'no'),
    };

    return {
      key: [
        translation.key,
        ...translation.entries.map((entries) => entries[1]),
        ...translation.overwrittenEntries.map((entries) => entries[1]).filter(isNotBlank),
      ].join(' '),
      meta,
    };
  }

  public downloadTranslations() {
    this.downloading = true;

    this.portalDataService.branding.data$.subscribe((branding) => {
      this.portalTranslateService
        .createTranslationZip()
        .pipe(first())
        .subscribe((blob) => {
          const date = format(new Date(), `yyyy-MM-dd_HH-mm-ss`);
          const name = branding.themeName;
          const version = environment.version;

          saveAs(blob, `translations_${name}_${version}_${date}.zip`);

          this.unsavedChangesService.reset();
          this.downloading = false;
        });
    });
  }

  public openTranslateEditor(key: string) {
    this.dialogService.open(TranslationEditComponent).withInput({
      key,
    });
  }
}
